// @flow

import React, { useState } from 'react';
import DocumentTitle from 'react-document-title';
import {
  Box,
  Button,
  Stack,
  Typography,
} from '@fortress-technology-solutions/fortress-component-library/Atoms';
import { AddIcon } from '@fortress-technology-solutions/fortress-component-library/Icons';
import ElementWithPermissions from '../../components/ElementWithPermissions';
import useHistory from '../../hooks/useHistory';
import useIntl from '../../hooks/useIntl';
import useSelectedProperty from '../../hooks/useSelectedProperty';
import messages from './messages';
import ManageGrossRentsTabs from './ManageGrossRentsTabs';
import GrossRentChangesTab from './GrossRentChangesTab';
import GrossRentCertificationsTab from './GrossRentCertificationsTab';
import { TAB, DEFAULT_TAB } from './constants';
import AddGrossRentLimitsWizard from './AddGrossRentLimitsWizard';
import type { ManageGrossRentsTabValue } from './types';

const NAME = 'manage-gross-rents';
const TAB_QUERY_PARAM = 'tab';

const ManageGrossRents = () => {
  const intl = useIntl();
  const history = useHistory();
  const selectedProperty = useSelectedProperty();
  const propertyName = selectedProperty?.name ?? '';
  const [isWizardOpen, setIsWizardOpen] = useState(false);

  const tabFromUrl = new URLSearchParams(history.location.search).get(
    TAB_QUERY_PARAM,
  );
  const selectedTab =
    tabFromUrl === TAB.GROSS_RENT_CHANGES ||
    tabFromUrl === TAB.GROSS_RENT_CERTIFICATIONS
      ? tabFromUrl
      : DEFAULT_TAB;
  const showAddButton = selectedTab === TAB.GROSS_RENT_CHANGES;

  const handleTabChange = (_event: mixed, value: ManageGrossRentsTabValue) => {
    const searchParams = new URLSearchParams(history.location.search);
    searchParams.set(TAB_QUERY_PARAM, value);
    history.replace({
      pathname: history.location.pathname,
      search: `?${searchParams.toString()}`,
    });
  };

  return (
    <DocumentTitle
      data-testid={NAME}
      title={intl.formatMessage(messages.pageTitle, { propertyName })}
    >
      <Stack p={2} pt={3}>
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
          mb={2}
        >
          <Typography variant="titleLarge">
            {intl.formatMessage(messages.header)}
          </Typography>
          <Box
            sx={{
              flexShrink: 0,
              display: 'flex',
              alignItems: 'center',
              minHeight: (theme) => theme.spacing(5),
            }}
          >
            {showAddButton ? (
              <ElementWithPermissions
                scope={['add-hud-gross-rent-limits']}
                showTooltip
                tooltip={{
                  id: 'add-hud-gross-rent-limits-tooltip',
                  title: intl.formatMessage(messages.noPermissionTooltip),
                  placement: 'top',
                  arrow: true,
                }}
              >
                <Button
                  variant="shout"
                  startIcon={<AddIcon />}
                  onClick={() => setIsWizardOpen(true)}
                >
                  {intl.formatMessage(messages.GrossRentLimits)}
                </Button>
              </ElementWithPermissions>
            ) : null}
          </Box>
        </Stack>

        <ManageGrossRentsTabs
          selectedTab={selectedTab}
          onChange={handleTabChange}
        />

        {selectedTab === TAB.GROSS_RENT_CHANGES ? (
          <GrossRentChangesTab />
        ) : (
          <GrossRentCertificationsTab />
        )}

        <AddGrossRentLimitsWizard
          open={isWizardOpen}
          closeModal={() => setIsWizardOpen(false)}
        />
      </Stack>
    </DocumentTitle>
  );
};

export default ManageGrossRents;
